import jsPDF from "jspdf";
import {
    brand,
    drawFrame,
    drawLogoPreserved,
    caseTransform,
    priceLabel,
} from "./pdfBrandHelpers";

/**
 * PDF "Magic Menu" / Ricettario — A4, brand-aware.
 * Niente banda colorata in alto. Cornice opzionale. Logo proporzionato.
 * Nessuna riga di separazione tra i piatti.
 */
export function downloadMagicMenuPdf(menu, brandOpts = {}) {
    const B = brand(brandOpts);
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210;
    const H = 297;
    const margin = 22;
    const contentW = W - margin * 2;
    const { primary, accent, ink, muted, paper } = B.rgb;
    const STYLE = B.style;
    const F = B.fonts;

    doc.setFillColor(...paper);
    doc.rect(0, 0, W, H, "F");
    drawFrame(doc, W, H, margin, STYLE, B.rgb);

    let y = margin;

    // Header
    if (B.useLogo && B.logoDataUrl) {
        const used = drawLogoPreserved(doc, B.logoDataUrl, W / 2, y, 60, 28);
        y += (used || 0) + 6;
    } else if (menu.restaurant_name) {
        doc.setFont(F.subtitle.family, F.subtitle.weight);
        doc.setFontSize(13);
        doc.setTextColor(...primary);
        doc.text(
            caseTransform(menu.restaurant_name, STYLE.nameCase),
            W / 2,
            y + 5,
            { align: "center" },
        );
        y += 10;
    }

    // Eyebrow Magic Chef + data
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...accent);
    const dateStr = new Date(menu.created_at || Date.now())
        .toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })
        .toUpperCase();
    doc.text(`MAGIC CHEF · ${dateStr}`, W / 2, y, { align: "center" });
    y += 7;

    // Titolo
    doc.setFont(F.title.family, F.title.weight);
    doc.setFontSize(STYLE.titleSize - 2);
    doc.setTextColor(...primary);
    // Sposta y alla baseline per evitare overlap con header
    y += (STYLE.titleSize - 2) * 0.42;
    const titleText = caseTransform(menu.title || "Menu", STYLE.nameCase === "upper" ? "upper" : "normal");
    const titleLines = doc.splitTextToSize(titleText, contentW);
    const titleAlign = STYLE.align === "left" ? "left" : "center";
    const titleX = STYLE.align === "left" ? margin : W / 2;
    titleLines.forEach((line, i) => {
        doc.text(line, titleX, y + i * ((STYLE.titleSize - 2) * 0.42), { align: titleAlign });
    });
    y += titleLines.length * ((STYLE.titleSize - 2) * 0.42) + 2;

    if (menu.subtitle) {
        doc.setFont(F.subtitle.family, F.subtitle.weight);
        doc.setTextColor(...muted);
        doc.setFontSize(10.5);
        const subLines = doc.splitTextToSize(menu.subtitle, contentW);
        subLines.forEach((line, i) => {
            doc.text(line, titleX, y + i * 5, { align: titleAlign });
        });
        y += subLines.length * 5 + 2;
    }

    // Meta
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...muted);
    const meta = [];
    if (menu.diners) meta.push(`Per ${menu.diners} commensali`);
    if (menu.style) meta.push(menu.style);
    if ((menu.dishes || []).length) meta.push(`${menu.dishes.length} piatti`);
    if (meta.length) {
        doc.text(meta.join("  ·  ").toUpperCase(), titleX, y, { align: titleAlign });
        y += 6;
    }
    if (STYLE.id === "classic" || STYLE.id === "vintage") {
        doc.setDrawColor(...accent);
        doc.setLineWidth(STYLE.id === "vintage" ? 0.7 : 0.4);
        const lineX = STYLE.align === "left" ? margin : W / 2 - 12;
        doc.line(lineX, y, lineX + 24, y);
        y += 5;
    } else {
        y += 2;
    }

    const ensureSpace = (need) => {
        if (y + need > H - 22) {
            doc.addPage();
            doc.setFillColor(...paper);
            doc.rect(0, 0, W, H, "F");
            drawFrame(doc, W, H, margin, STYLE, B.rgb);
            y = margin;
        }
    };

    // Piatti
    (menu.dishes || []).forEach((d, idx) => {
        ensureSpace(20);

        // Course eyebrow
        if (d.course) {
            doc.setFont("helvetica", STYLE.eyebrowStyle === "italic" ? "bolditalic" : "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...accent);
            doc.text(d.course.toUpperCase(), margin, y);
            y += 4.5;
        }

        // Nome piatto
        doc.setFont(F.dish.family, F.dish.weight);
        doc.setFontSize(STYLE.nameSize);
        doc.setTextColor(...primary);
        const nameText = caseTransform(d.name || "—", STYLE.nameCase);
        const nameLines = doc.splitTextToSize(nameText, contentW);
        doc.text(nameLines, margin, y);
        y += nameLines.length * (STYLE.nameSize * 0.42);

        if (d.description) {
            doc.setFont(F.subtitle.family, F.subtitle.weight);
            doc.setFontSize(10);
            doc.setTextColor(...ink);
            const descLines = doc.splitTextToSize(d.description, contentW);
            ensureSpace(descLines.length * 4.5 + 4);
            doc.text(descLines, margin, y);
            y += descLines.length * 4.5 + 1;
        }

        const dishMeta = [];
        const total = (Number(d.prep_time_min) || 0) + (Number(d.cook_time_min) || 0);
        if (total > 0) dishMeta.push(`${total} min`);
        if (d.difficulty) dishMeta.push(d.difficulty);
        if (dishMeta.length) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(...muted);
            doc.text(dishMeta.join("  ·  "), margin, y);
            y += 5;
        }

        // Allergeni (sempre visibili — obbligatori per legge sul menu)
        if (d.allergens && d.allergens.length) {
            ensureSpace(6);
            doc.setFont(F.allergens?.family || "helvetica", F.allergens?.weight || "italic");
            doc.setFontSize(8);
            doc.setTextColor(...muted);
            const allergText = `Allergeni: ${d.allergens.join(" · ")}`;
            const allergLines = doc.splitTextToSize(allergText, contentW);
            doc.text(allergLines, margin, y);
            y += allergLines.length * 3.6 + 1.5;
        }

        if (d.ingredients_used && d.ingredients_used.length) {
            ensureSpace(8);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...accent);
            doc.text("INGREDIENTI", margin, y);
            y += 4;
            doc.setFont(STYLE.font.body, "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(...ink);
            const colW = contentW / 2;
            d.ingredients_used.forEach((ing, i) => {
                const col = i % 2;
                const row = Math.floor(i / 2);
                const x = margin + col * colW;
                const lineY = y + row * 5;
                ensureSpace(row * 5 + 5);
                const qty = ing.quantity != null
                    ? ` — ${ing.quantity}${ing.unit ? " " + ing.unit : ""}`
                    : "";
                doc.text(`• ${ing.name}${qty}`, x, lineY);
            });
            const rows = Math.ceil(d.ingredients_used.length / 2);
            y += rows * 5 + 1;
        }

        if (d.instructions && d.instructions.length) {
            ensureSpace(8);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(7.5);
            doc.setTextColor(...accent);
            doc.text("PREPARAZIONE", margin, y);
            y += 4;
            doc.setFont(STYLE.font.body, "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(...ink);
            d.instructions.forEach((step, i) => {
                const stepText = `${i + 1}. ${step}`;
                const lines = doc.splitTextToSize(stepText, contentW - 4);
                ensureSpace(lines.length * 4.5 + 1);
                doc.text(lines, margin + 2, y);
                y += lines.length * 4.5 + 1;
            });
        }

        // Prezzo opzionale a fondo piatto
        const priceText = priceLabel(d.suggested_price_eur);
        if (priceText) {
            doc.setFont(F.price.family, F.price.weight);
            doc.setFontSize(11);
            doc.setTextColor(...accent);
            doc.text(`Prezzo consigliato: ${priceText}`, margin, y + 1);
            y += 5;
        }

        // Niente linea di separazione — solo whitespace
        if (idx < (menu.dishes || []).length - 1) {
            y += STYLE.spacing;
        }
    });

    // Dispensa suggerita (riquadro colorato)
    if (menu.suggested_pantry?.length) {
        ensureSpace(20);
        y += 4;
        doc.setDrawColor(...accent);
        doc.setLineWidth(0.4);
        doc.rect(margin, y, contentW, 14);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...accent);
        doc.text("DISPENSA SUGGERITA", margin + 4, y + 5);
        doc.setFont(STYLE.font.body, "normal");
        doc.setFontSize(9);
        doc.setTextColor(...ink);
        const pantryLine = doc.splitTextToSize(menu.suggested_pantry.join(" · "), contentW - 8);
        doc.text(pantryLine, margin + 4, y + 10);
        y += 18;
    }

    // Footer
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
        doc.setPage(p);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(7);
        doc.setTextColor(...muted);
        doc.text(
            `Generato da RENOVA360 Magic Chef · pag. ${p}/${pages}`,
            W / 2,
            H - 12,
            { align: "center" },
        );
    }

    const safe = (menu.title || "Magic Menu")
        .replace(/[^a-zA-Z0-9 _-]/g, "")
        .trim()
        .replace(/\s+/g, "_");
    doc.save(`${safe || "Magic_Menu"}.pdf`);
}
